import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints, baseQuery, formatErrors } from "utils/api/endpoints";
import { 
  DashboardState, 
  CountriesListResponse, 
  RoamingCountriesListResponse, 
  DashboardData,
  UpgradePlanParams,
  UpgradePlanResponse
} from "./types";

export const initialState: DashboardState = {
  loading: false,
  error: null,
  data: null,
};

const slice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setData(state, action: PayloadAction<DashboardData>) {
      state.data = action.payload;
    },
  },
});

// Create RTK Query API
export const api = createApi({
  reducerPath: "dashboardApi",
  baseQuery,
  tagTypes: ["Dashboard", "Countries"],
  endpoints: (builder) => ({
    getDashboardData: builder.query<{ data: DashboardData }, { customerId: string }>({
      query: (params) => ({
        ...endpoints.dashboard,
        params,
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 30000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching dashboard data',
        };
      },
    }),
    getCountriesList: builder.query<CountriesListResponse, any>({
      query: (params) => ({
        ...endpoints.countriesList,
        params,
      }),
      providesTags: ["Countries"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching countries list',
        };
      },
    }),
    getRoamingCountries: builder.query<RoamingCountriesListResponse, any>({
      query: (params) => ({
        ...endpoints.listOfRoamingCountries,
        params,
      }),
      providesTags: ["Countries"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching countries list',
        };
      },
    }),
    getAddonPlans: builder.query<any, any>({
      query: (params) => ({
        ...endpoints.getAddonPlans,
        params,
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching addon plans',
        };
      },
    }),
    upgradePlan: builder.query<any, { customerId: string }>({
      query: (params) => ({
        ...endpoints.upgradePlan,
        params,
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching upgrade plan details',
        };
      },
    }),
    getNewLine: builder.query<any, { customerId: string; line: number }>({
      query: (params) => ({
        ...endpoints.getNewLine,
        params,
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching new line details',
        };
      },
    }),
    buyDataTopUp: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.buyDataTopUp,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Dashboard'],
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while processing data top up',
        };
      },
    }),
    getUpgradedPlan: builder.query<UpgradePlanResponse, UpgradePlanParams>({
      query: (params) => ({
        ...endpoints.getUpgradedPlan,
        params: {
          customerId: params.customerId,
          networkType: params.networkType,
          plan: params.plan,
          month: params.month,
        },
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching upgraded plan details',
        };
      },
    }),
    upgradePlanOrderSummary: builder.query<any, any>({
      query: (params) => ({
        ...endpoints.upgradePlanOrderSummary,
        params,
      }),
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching upgrade plan order summary',
        };
      },
    }),
    upgradePlanCheckout: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.upgradePlanCheckout,
        body,
      }),
      invalidatesTags: ['Dashboard'],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
				return formatErrors(baseQueryReturnValue.data);
			},
    }),
    generateBillPdf: builder.query<Blob, any>({
      query: (params) => ({
        ...endpoints.generateBillPdf,
        params,
        responseHandler: async (response) => {
          // Handle PDF blob response
          if (response.ok) {
            return response.blob();
          }
          throw new Error('Failed to generate bill PDF');
        },
      }),
      providesTags: ["Dashboard"],
      // Add retry logic
      extraOptions: {
        maxRetries: 2,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 15000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while generating bill PDF',
        };
      },
    }),
  }),
});

export const { actions: dashboardActions, reducer: dashboardReducer } = slice;

export const useDashboardSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  
  return { 
    actions: slice.actions,
    ...api
  };
};

/**
 * Example Usage:
 *
 * export function MyComponent() {
 *   const { useLazyGetCountriesListQuery } = useDashboardSlice();
 *   const [getCountries, { data, isLoading, isError }] = useLazyGetCountriesListQuery();
 *
 *   useEffect(() => {
 *     // Call the API
 *     getCountries();
 *   }, []);
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isError) return <div>Error loading countries</div>;
 *
 *   return (
 *     <div>
 *       {data?.data.map(country => (
 *         <div key={country._id}>
 *           {country.destination} - {country.minutes} minutes
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 */
